import React, { useMemo } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import ChartComponent from './ChartComponent';
import { Customer, DataPoint } from '../../types';

interface ChartsContainerProps {
  loading: boolean;
  data: DataPoint[];
  customers: Customer[];
  regionCustomers: Customer[];
  startDate: Date | null;
  endDate: Date | null;
  selectedCustomerIds: string[];
}

interface SelectedCustomersData {
  [key: string]: {
    name: string;
    accountNumber: string | null;
    data: DataPoint[];
    engagementData: any;
  };
}

function buildRegionLookup(regionCustomers: Customer[]): Record<string, Customer> {
  const lookup: Record<string, Customer> = {};
  regionCustomers.forEach((c) => {
    lookup[c.id] = c;
  });
  return lookup;
}

function buildCustomerDataMap(
  customers: Customer[],
  accountById: Record<string, Customer>
): SelectedCustomersData {
  const map: SelectedCustomersData = {};
  customers.forEach((cust) => {
    const account = accountById[cust.id];

    map[cust.id] = {
      name: cust.name,
      accountNumber: account?.account_number ?? null,
      data: cust.data,
      engagementData: (cust as any).engagementData,
    };
  });
  return map;
}

const ChartsContainer: React.FC<ChartsContainerProps> = ({
  loading,
  data,
  customers,
  regionCustomers,
  startDate,
  endDate,
  selectedCustomerIds,
}) => {
  const regionLookup = useMemo(
    () => buildRegionLookup(regionCustomers),
    [regionCustomers]
  );

  const allRegionCustomers = useMemo(() => {
    const regionIds = new Set(regionCustomers.map((c) => c.id));
    return customers.filter((c) => regionIds.has(c.id));
  }, [regionCustomers, customers]);

  const regionCustomersData = useMemo(
    () => buildCustomerDataMap(allRegionCustomers, regionLookup),
    [allRegionCustomers, regionLookup]
  );

  const selectedCustomersData = useMemo(() => {
    const selected = customers.filter((c) => selectedCustomerIds.includes(c.id));
    return buildCustomerDataMap(selected, regionLookup);
  }, [customers, selectedCustomerIds, regionLookup]);

  if (!startDate || !endDate) {
    return <div>Please select a date range.</div>;
  }

  return (
    <div style={{ position: 'relative', minHeight: 400 }}>
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
            backgroundColor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </div>
      )}

      <ChartComponent
        data={data}
        title="Collision Risk Over Time"
        startDate={startDate}
        endDate={endDate}
        regionCustomersData={regionCustomersData}
        selectedCustomersData={selectedCustomersData}
      />
    </div>
  );
};

export default ChartsContainer;
